import { useRef, useState, type Dispatch, type SetStateAction } from "react";

type Tone = "neutral" | "warning";

type UseSalesConsoleVoiceCaptureParams = {
  setBulkNotes: Dispatch<SetStateAction<string>>;
};

export function useSalesConsoleVoiceCapture({ setBulkNotes }: UseSalesConsoleVoiceCaptureParams) {
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceHint, setVoiceHint] = useState("");
  const [voiceHintTone, setVoiceHintTone] = useState<Tone>("neutral");
  const voiceAppendRef = useRef("");
  const recognitionRef = useRef<any>(null);
  const idleSilencePollRef = useRef<number | null>(null);
  const lastSpeechActivityRef = useRef<number>(0);
  const voiceEndReasonRef = useRef<"idle" | "user" | "error" | "engine" | null>(null);

  function stopVoiceCapture() {
    if (!recognitionRef.current) return;
    voiceEndReasonRef.current = "user";
    if (idleSilencePollRef.current) window.clearInterval(idleSilencePollRef.current);
    idleSilencePollRef.current = null;
    try {
      recognitionRef.current.stop();
    } catch {
      /* noop */
    }
    recognitionRef.current = null;
    setVoiceBusy(false);
    setVoiceHint("Stopped: you pressed Stop listening.");
    setVoiceHintTone("neutral");
  }

  async function onTranscribeIntoBulkNotes() {
    const speechWindow = window as unknown as { SpeechRecognition?: new () => any; webkitSpeechRecognition?: new () => any };
    const SpeechRecognitionImpl = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!SpeechRecognitionImpl) return;
    if (recognitionRef.current) stopVoiceCapture();

    const SILENCE_MS = 28000;
    const POLL_MS = 400;
    voiceEndReasonRef.current = null;
    setVoiceBusy(true);
    setVoiceHint("Listening for bulk notes… Stops after ~28s with no new speech (not a fixed session timer).");
    setVoiceHintTone("neutral");

    const recognition = new SpeechRecognitionImpl();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    voiceAppendRef.current = "";
    lastSpeechActivityRef.current = Date.now();
    const bumpActivity = () => {
      lastSpeechActivityRef.current = Date.now();
    };
    recognition.onspeechstart = bumpActivity;
    recognition.onsoundstart = bumpActivity;

    const clearSilencePoll = () => {
      if (idleSilencePollRef.current) window.clearInterval(idleSilencePollRef.current);
      idleSilencePollRef.current = null;
    };
    const startSilencePoll = () => {
      clearSilencePoll();
      idleSilencePollRef.current = window.setInterval(() => {
        if (Date.now() - lastSpeechActivityRef.current < SILENCE_MS) return;
        clearSilencePoll();
        voiceEndReasonRef.current = "idle";
        setVoiceHint("Stopped: ~28 seconds without new speech (silence). Your text so far is kept — press Speak notes to add more.");
        setVoiceHintTone("warning");
        try {
          recognition.stop();
        } catch {
          /* already stopped */
        }
      }, POLL_MS);
    };
    startSilencePoll();

    recognition.onresult = (event: any) => {
      bumpActivity();
      let combinedFinal = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i]?.isFinal) combinedFinal += event.results[i]?.[0]?.transcript ?? "";
      }
      const nextFinal = combinedFinal.trim();
      if (!nextFinal) return;
      voiceAppendRef.current = `${voiceAppendRef.current}${voiceAppendRef.current ? " " : ""}${nextFinal}`.trim();
      setBulkNotes((prev) => `${prev}${prev ? " " : ""}${nextFinal}`.trim());
    };
    recognition.onerror = (ev: any) => {
      const code = String(ev?.error || "");
      if (code === "aborted" && (voiceEndReasonRef.current === "idle" || voiceEndReasonRef.current === "user")) return;
      clearSilencePoll();
      voiceEndReasonRef.current = "error";
      setVoiceBusy(false);
      voiceAppendRef.current = "";
      recognitionRef.current = null;
      if (code === "aborted" || code === "no-speech") {
        setVoiceHint(code === "no-speech" ? "Stopped: no speech detected (engine). Try again or speak closer to the mic — text so far is kept." : "Stopped: recognition aborted (browser). Text so far is kept — try Speak notes again.");
      } else {
        setVoiceHint(`Stopped: voice error (${code || "unknown"}). Text so far is kept — try Speak notes again.`);
      }
      setVoiceHintTone("neutral");
      try {
        recognition.stop();
      } catch {
        /* noop */
      }
    };
    recognition.onend = () => {
      clearSilencePoll();
      setVoiceBusy(false);
      voiceAppendRef.current = "";
      recognitionRef.current = null;
      const reason = voiceEndReasonRef.current;
      voiceEndReasonRef.current = null;
      if (reason === "idle" || reason === "error" || reason === "user") return;
      setVoiceHint("Listening session ended (browser). Text so far is kept — press Speak notes to continue.");
      setVoiceHintTone("neutral");
    };
    recognition.start();
  }

  function cleanupVoiceCapture() {
    if (idleSilencePollRef.current) window.clearInterval(idleSilencePollRef.current);
    if (recognitionRef.current) recognitionRef.current.stop();
  }

  return { voiceBusy, voiceHint, voiceHintTone, stopVoiceCapture, onTranscribeIntoBulkNotes, cleanupVoiceCapture };
}

